<script>
import { createEventDispatcher } from 'svelte';
import { format, endOfMonth, startOfMonth, startOfWeek, addDays, setDay, isSameDay, isSameMonth, addMonths } from 'date-fns';
import { tooltipTop } from '../tooltip';
import { today } from '../data/datetime';


export let month = undefined;
export let selected = undefined;
export let navigatable = true;
export let min = null;
export let max = null;
const dateOptions = {};
const dispatch = createEventDispatcher();
let currentMonth = startOfMonth(month || selected || $today);
let weeks;
let weekDays = [0, 1, 2, 3, 4, 5, 6].map(i => format(setDay($today, i, dateOptions), 'EEE', dateOptions));
const monthFormat = 'MMMM yyyy';

$: weeks = calculateWeeks(currentMonth, $today);



function calculateWeeks(currentMonth, today) {
  const end = endOfMonth(currentMonth);
  const weeks = [];
  let day = setDay(currentMonth, 0, dateOptions);
  for (let i = 0; i < 6; i++) {
    if (day > end) break;
    const week = [];
    weeks.push(week);
    for (let j = 0; j < 7; j++) {
      week.push(day);
      day = addDays(day, 1);
    }
  }
  return weeks;
}

function available(day) {
  return (!min || min <= day) && (!max || max >= day);
}

function onClick(day) {
  if (!available(day)) return;
  if (!isSameMonth(day, currentMonth)) {
    if (navigatable) currentMonth = startOfMonth(day);
    return;
  }
  selectDayByDate(day);
}

function nextMonth(direction) {
  currentMonth = addMonths(currentMonth, direction);
}

function selectDayByDate(date) {
  selected = isSameDay(date, selected) ? null : date;
  dispatch('select', { selected });
}
</script>

{#if currentMonth}
<div class="calendar">
  <div class="month-header">
    {#if navigatable}
      <button class="icon icon-chevron-left"
        disabled={min && min > addDays(currentMonth, -1)}
        on:click={() => nextMonth(-1)}
        use:tooltipTop={format(addMonths(currentMonth, -1), monthFormat, dateOptions)}
      ></button>
    {/if}
    <span class="month-name">{format(currentMonth, monthFormat, dateOptions)}</span>
    {#if navigatable}
      <button class="icon icon-chevron-right"
        disabled={max && max < addMonths(currentMonth, 1)}
        on:click={() => nextMonth(1)}
        use:tooltipTop={format(addMonths(currentMonth, 1), monthFormat, dateOptions)}
      ></button>
    {/if}
  </div>
  <div class="month-body">
    <div class="day-names">
      {#each weekDays as day, index}
      <span class="day-name">{day}</span>
      {/each}
    </div>
    <div class="weeks">
      {#each weeks as week}
      <div class="week">
        {#each week as day}
        <div
          class="day"
          class:out={!isSameMonth(day, currentMonth)}
          class:unavailable={!available(day)}
          class:selected={isSameDay(day, selected)}
          class:today={isSameDay(day, $today)}
          on:click={() => onClick(day)}
        >{format(day, 'd', dateOptions)}</div>
        {/each}
      </div>
      {/each}
    </div>
  </div>
</div>
{/if}

<style>
.calendar {
  width: 296px;
  flex: 0 0 296px;
}
:global(.size-mobile) .calendar {
  width: 100%;
}
.calendar .month-header, .calendar .month-body {
  background: var(--warm-grey-050);
  border: 1px solid var(--warm-grey-100);
}
.calendar .month-header {
  display: flex;
  align-items: center;
  height: 42px;
  color: var(--warm-grey-600);
  font-size: 20px;
  line-height: 38px;
  text-align: center;
  padding: 2px 8px;
  margin-bottom: 2px;
}
.month-header .icon {
  font-size: 26px;
}
.calendar .month-header .month-name {
  flex: 1 1 auto;
}
.calendar .day-names, .calendar .week {
  display: flex;
  padding: 0 1px;
  user-select: none;
}
.calendar .weeks {
  padding: 2px 0;
}
.calendar .day-names {
  padding-bottom: 2px;
}
.calendar .day-name {
  flex: 1;
  margin: 1px;
  line-height: 16px;
  font-size: var(--font-size-050);
  color: var(--warm-grey-600);
  text-align: center;
}
.calendar .day {
  flex: 1;
  margin: 1px 1px 2px;
  height: 32px;
  font-size: 16px;
  line-height: 32px;
  text-align: center;
  color: var(--warm-grey-600);
  background: #fff;
  box-shadow: 0 1px var(--warm-grey-200);
  cursor: pointer;
  transition: box-shadow .1s linear, color .1s linear;
}
.calendar .day:not(.out):not(.unavailable):hover {
  position: relative;
  color: var(--warm-grey-700);
  box-shadow: 0 1px 3px var(--warm-grey-300);
}
.calendar .day:not(.out):not(.unavailable):active {
  box-shadow: inset 0 1px 3px var(--warm-grey-300);
}
.calendar .day.out, .calendar .day.out:hover,
.calendar .day.unavailable, .calendar .day.unavailable:hover {
  background: none;
  box-shadow: none;
}
.calendar .day.out, .calendar .day.out:hover {
  color: var(--warm-grey-400);
}
.calendar .day.out:hover {
  color: var(--warm-grey-600);
}
.calendar .day.unavailable, .calendar .day.unavailable:hover {
  cursor: default;
  color: var(--warm-grey-100);
}
.calendar .day.today {
  font-weight: bold;
  padding-top: 3px;
  line-height: 29px;
}
.calendar .day.selected {
  position: relative;
  color: var(--cyan-700);
  outline: 3px double var(--cyan-400);
  outline-offset: -2px;
}
.calendar .day.selected:hover {
  color: var(--cyan-700);
}
</style>
